import { Component } from '@theme/component';
import { fetchConfig } from '@theme/utilities';
import { CartAddEvent, CartErrorEvent } from '@theme/events';

/**
 * A custom element that handles one-click add to cart for sample variants.
 *
 * @extends Component
 */
class OrderSampleButtonComponent extends Component {
    /** @type {HTMLButtonElement | null} */
    #button = null;
    /** @type {((event: Event) => void) | null} */
    #clickHandler = null;

    connectedCallback() {
        super.connectedCallback();
        this.#button = this.querySelector('button');
        if (this.#button) {
            const handler = this.#handleClick.bind(this);
            this.#clickHandler = handler;
            this.#button.addEventListener('click', handler);
        }
    }

    disconnectedCallback() {
        super.disconnectedCallback();
        if (this.#button && this.#clickHandler) {
            this.#button.removeEventListener('click', this.#clickHandler);
        }
    }

    /**
     * Handles the click event for the sample button.
     *
     * @param {Event} event - The click event.
     */
    #handleClick(event) {
        event.preventDefault();
        if (!this.#button || this.#button.disabled) return;

        const variantId = this.dataset.variantId;
        const productId = this.dataset.productId;
        const sectionId = this.dataset.sectionId;

        if (!variantId) {
            return;
        }

        // Disable button during request
        this.#button.disabled = true;
        const originalHTML = this.#button.innerHTML;
        const iconElement = this.#button.querySelector('.order-sample-button__icon');
        const iconHTML = iconElement ? iconElement.outerHTML : '';
        
        // Update button text while preserving icon
        if (iconElement) {
            this.#button.innerHTML = iconHTML + ' Adding...';
        } else {
            this.#button.textContent = 'Adding...';
        }

        // Create FormData - try to use form if available (PDP), otherwise create directly (product cards)
        let formData;
        const productFormComponent = this.closest('product-form-component');
        
        if (productFormComponent) {
            // PDP context: use the form
            const form = productFormComponent.querySelector('form[data-type="add-to-cart-form"]');
            if (form instanceof HTMLFormElement) {
                // Get the variant ID input and temporarily change it to the sample variant
                const variantIdInput = form.querySelector('input[name="id"]');
                if (variantIdInput instanceof HTMLInputElement) {
                    // Store original value and set to sample variant
                    const originalVariantId = variantIdInput.value;
                    variantIdInput.value = variantId;
                    formData = new FormData(form);
                    // Restore original variant ID
                    variantIdInput.value = originalVariantId;
                } else {
                    // Fallback: create FormData directly
                    formData = new FormData();
                    formData.set('id', variantId);
                    formData.set('quantity', '1');
                }
            } else {
                // Fallback: create FormData directly
                formData = new FormData();
                formData.set('id', variantId);
                formData.set('quantity', '1');
            }
        } else {
            // Product card context: create FormData directly
            formData = new FormData();
            formData.set('id', variantId);
            formData.set('quantity', '1');
        }

        // Get cart items components for section updates
        const cartItemsComponents = document.querySelectorAll('cart-items-component');
        /** @type {string[]} */
        const cartItemComponentsSectionIds = [];
        cartItemsComponents.forEach((item) => {
            if (item instanceof HTMLElement && item.dataset.sectionId) {
                cartItemComponentsSectionIds.push(item.dataset.sectionId);
            }
        });

        // Append sections (matching product-form.js pattern)
        if (cartItemComponentsSectionIds.length > 0) {
            formData.append('sections', cartItemComponentsSectionIds.join(','));
        }

        const fetchCfg = fetchConfig('javascript', { body: formData });

        fetch(Theme.routes.cart_add_url, {
            ...fetchCfg,
            headers: {
                ...fetchCfg.headers,
                Accept: 'text/html',
            },
        })
            .then((response) => response.json())
            .then((response) => {
                if (response.status) {
                    // Error occurred - show user-friendly message
                    const errorMessage = response.message || response.description || 'Failed to add item to cart';

                    // Update button text to show error
                    if (this.#button) {
                        const errorText = errorMessage.includes('sold out') ? 'Sold Out' : 'Error';
                        if (iconElement) {
                            this.#button.innerHTML = iconHTML + ' ' + errorText;
                        } else {
                            this.#button.textContent = errorText;
                        }
                        this.#button.disabled = false;

                        // Reset button text after 3 seconds
                        setTimeout(() => {
                            if (this.#button) {
                                this.#button.innerHTML = originalHTML;
                            }
                        }, 3000);
                    }

                    this.dispatchEvent(
                        new CartErrorEvent(
                            `order-sample-button-${sectionId}`,
                            errorMessage,
                            response.description,
                            response.errors
                        )
                    );
                } else {
                    // Success - dispatch cart update event
                    this.dispatchEvent(
                        new CartAddEvent({}, variantId.toString(), {
                            source: 'order-sample-button',
                            itemCount: 1,
                            productId: productId,
                            sections: response.sections,
                        })
                    );

                    // Show success feedback
                    if (this.#button) {
                        if (iconElement) {
                            this.#button.innerHTML = iconHTML + ' Added!';
                        } else {
                            this.#button.textContent = 'Added!';
                        }
                        setTimeout(() => {
                            if (this.#button) {
                                this.#button.innerHTML = originalHTML;
                                this.#button.disabled = false;
                            }
                        }, 2000);
                    }
                }
            })
            .catch((error) => {
                this.dispatchEvent(
                    new CartErrorEvent(
                        `order-sample-button-${sectionId}`,
                        error.message || 'Failed to add item to cart',
                        '',
                        {}
                    )
                );
                if (this.#button) {
                    this.#button.innerHTML = originalHTML;
                    this.#button.disabled = false;
                }
            });
    }
}

if (!customElements.get('order-sample-button')) {
    customElements.define('order-sample-button', OrderSampleButtonComponent);
}
